import { type QueryClient } from '@tanstack/react-query';
import Fuse from 'fuse.js';

import { campaignsEndpoints } from '@/api/endpoints';
import { type Campaign } from '@/shared/types/campaigns';

import { ENTITY_TYPES, type EntityType, type PageDrillDown, type PageEntry } from './types';

// ============================================================================
// Constants
// ============================================================================

/** Maximum number of entity search results to show per type */
export const MAX_PER_TYPE = 5;

/** Maps page IDs to their drill-down entity types */
export const PAGE_DRILL_DOWN_IDS: Record<string, PageDrillDown> = {
  'page-ai-agents': 'ai_agent',
  'page-knowledge-bases': 'knowledge_base',
};

/** Maps entity types to their i18n translation keys */
export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  ai_agent: 'common.ai_agents',
  knowledge_base: 'common.collections',
  campaign: 'common.campaigns',
  conversation: 'common.conversation',
  environment: 'main_navigation.environments',
  integration: 'main_navigation.integrations',
  rule: 'common.rules',
};

// ============================================================================
// Type Guards
// ============================================================================

const entityTypeSet = new Set<string>(ENTITY_TYPES);

/** Type guard to validate if a string is a valid EntityType */
export const isEntityType = (value: string): value is EntityType => entityTypeSet.has(value);

// ============================================================================
// Path Matching
// ============================================================================

/**
 * Checks if a path matches or is a child of another path.
 *
 * Root path '/' only matches exactly. Other paths match if they are
 * equal or if the test path starts with the base path followed by '/'.
 *
 * Examples:
 * - matchesPath('/users', '/users') → true
 * - matchesPath('/users', '/users/123') → true
 * - matchesPath('/users', '/users-list') → false
 * - matchesPath('/', '/') → true
 * - matchesPath('/', '/users') → false
 */
export const matchesPath = (basePath: string, testPath: string): boolean => {
  if (basePath === '/') {
    return testPath === '/';
  }
  return testPath === basePath || testPath.startsWith(basePath + '/');
};

/** Regex pattern to extract desk ID from chat/environment paths */
const DESK_PATH_RE = /^\/(chats|environments)\/([a-f0-9-]+)/;

/**
 * Extracts desk ID from a path like "/chats/abc-123" or "/environments/xyz-456".
 * Returns null if the path doesn't match the expected format.
 */
export const extractDeskId = (path: string): string | null => DESK_PATH_RE.exec(path)?.[2] ?? null;

/**
 * Picks the longer of two page entries (null-safe).
 * Used to select the most specific page match.
 */
export const pickLongestMatch = (a: PageEntry | null, b: PageEntry | null): PageEntry | null => {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return a.path.length >= b.path.length ? a : b;
};

// ============================================================================
// Fuzzy Matching
// ============================================================================

const FUSE_OPTIONS = { includeScore: true, threshold: 0.3, ignoreLocation: true } as const;

/** Creates a Fuse instance if the items array is non-empty, otherwise returns null. */
export const makeFuse = <T>(items: T[] | undefined | null, keys: string[]): Fuse<T> | null =>
  items?.length ? new Fuse(items, { ...FUSE_OPTIONS, keys }) : null;

// ============================================================================
// String Parsing
// ============================================================================

/** Builds an entity value string like "entity-ai_agent-123" from type and id. */
export const buildEntityValue = (type: EntityType, id: string): string => `entity-${type}-${id}`;

/**
 * Parses an entity value string like "entity-ai_agent-123" into type and id.
 * Returns null if the value doesn't match the expected format or contains invalid type.
 */
export const parseEntityValue = (value: string): { type: EntityType; id: string } | null => {
  const match = value.match(/^entity-([^-]+)-(.+)$/);
  if (!match) {
    return null;
  }
  const type = match[1];
  if (!isEntityType(type)) {
    return null;
  }
  return { type, id: match[2] };
};

// ============================================================================
// Classification
// ============================================================================

export type SelectType = 'sub_page' | 'page' | 'entity';

/**
 * Determines the selection type based on drill-down state and highlighted value.
 */
export const getSelectType = (
  hasDrillDown: boolean,
  highlightedValue: string | undefined
): SelectType => {
  if (hasDrillDown) {
    return 'sub_page';
  }
  if (highlightedValue?.startsWith('page-')) {
    return 'page';
  }
  return 'entity';
};

/**
 * Checks if a command palette value can be drilled into with Tab key.
 *
 * - Without drill-down: page drill-down IDs, ai_agent entities, or environment entities
 * - With page drill-down: only ai_agent entities
 */
export const isDrillableValue = (value: string, pageDrillDown: PageDrillDown | null): boolean => {
  if (!pageDrillDown) {
    return (
      value in PAGE_DRILL_DOWN_IDS ||
      value.startsWith('entity-ai_agent-') ||
      value.startsWith('entity-environment-')
    );
  }
  return value.startsWith('entity-ai_agent-');
};

// ============================================================================
// Cache Operations
// ============================================================================

/**
 * Assembles all unique campaigns from the TanStack Query cache.
 *
 * Campaigns use infinite queries with dynamic keys, so we scan all
 * cache entries matching the 'campaigns' query key prefix and collect
 * unique campaigns across all pages.
 *
 * Note: The type assertion for TanStack Query data is necessary because
 * the query cache doesn't provide typed access to infinite query structures.
 */
export const assembleCampaignsFromCache = (queryClient: QueryClient): Campaign[] => {
  const campaignQueries = queryClient
    .getQueryCache()
    .findAll({
      predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === campaignsEndpoints.campaigns,
    });

  // Collect all unique campaigns from infinite query pages
  const seen = new Set<string>();
  const campaigns: Campaign[] = [];

  for (const entry of campaignQueries) {
    // Type assertion necessary: TanStack Query cache doesn't expose typed infinite query data
    const data = entry.state.data as { pages?: Array<{ campaigns?: Campaign[] }> } | undefined;
    if (!data?.pages) {
      continue;
    }

    for (const page of data.pages) {
      if (!page.campaigns) {
        continue;
      }

      for (const c of page.campaigns) {
        if (seen.has(c.campaign_id)) {
          continue;
        }
        seen.add(c.campaign_id);
        campaigns.push(c);
      }
    }
  }

  return campaigns;
};
